import React, { useState, useMemo } from 'react';
import {
    Box,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Business as BusinessIcon,
    CardMembership as LicenseIcon,
    Person as PersonIcon,
    CheckCircle as CheckIcon,
    ArrowBack as BackIcon,
    ArrowForward as ForwardIcon,
    Palette as BrandingIcon,
    Public as DomainIcon,
    Storage as DemoDataIcon,
} from '@mui/icons-material';
import { Title, useNotify, useRedirect, useTranslate } from 'react-admin';

import { CompanyStep } from './steps/CompanyStep';
import { DomainStep } from './steps/DomainStep';
import { BrandingStep } from './steps/BrandingStep';
import { LicenseStep } from './steps/LicenseStep';
import { MultiAdminStep } from './steps/MultiAdminStep';
import { DemoDataStep } from './steps/DemoDataStep';
import { SummaryStep } from './steps/SummaryStep';

export interface AdminEntry {
    email: string;
    firstName: string;
    lastName: string;
    role: string; // ROLE_COMPANY_ADMIN default
}

export interface OnboardingData {
    // Company
    companyName: string;
    companyEmail: string;
    companyPhone: string;
    companyStreet: string;
    companyZip: string;
    companyCity: string;
    companyCountry: string;
    companyWebsite: string;
    isDemoTenant: boolean; // Issue #269: Demo-Mode-Toggle in Step 1

    // Domain (Step 2)
    domainMode: 'subdomain' | 'custom';
    subdomainSlug: string; // wird mit *.app. oder *.demo. ergaenzt
    customDomain: string; // z. B. "academy.firma.de"

    // Branding (Step 3)
    logoUrl: string;
    primaryColor: string;
    secondaryColor: string;
    faviconUrl: string;
    appTitle: string;
    poweredByText: string;

    // License (Step 4 — production-only)
    productId: number | null;
    productName: string;
    licenseExpiresAt: string;
    maxUsers: string;

    // Admins (Step 5) — Multi-Eintrag
    admins: AdminEntry[];

    // Demo-Daten (Step 6 — demo-only)
    demoSourceCompanyId: number | null;
    demoExpiresInDays: number;
    demoSelection: {
        catalogs: boolean;
        courses: boolean;
        learningPaths: boolean;
        wiki: boolean;
        media: boolean;
        h5p: boolean;
    };
}

const DEFAULT_ADMIN: AdminEntry = {
    email: '',
    firstName: '',
    lastName: '',
    role: 'ROLE_COMPANY_ADMIN',
};

const initialData: OnboardingData = {
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyStreet: '',
    companyZip: '',
    companyCity: '',
    companyCountry: 'Deutschland',
    companyWebsite: '',
    isDemoTenant: false,

    domainMode: 'subdomain',
    subdomainSlug: '',
    customDomain: '',

    logoUrl: '',
    primaryColor: '#1cab7e',
    secondaryColor: '#1f3240',
    faviconUrl: '',
    appTitle: '',
    poweredByText: '',

    productId: null,
    productName: '',
    licenseExpiresAt: '',
    maxUsers: '',

    admins: [{ ...DEFAULT_ADMIN }],

    demoSourceCompanyId: null,
    demoExpiresInDays: 60,
    demoSelection: {
        catalogs: true,
        courses: true,
        learningPaths: true,
        wiki: true,
        media: true,
        h5p: true,
    },
};

interface StepDef {
    key: 'company' | 'domain' | 'branding' | 'license' | 'admins' | 'demoData' | 'summary';
    label: string;
    icon: React.ReactNode;
    showInDemo: boolean;
    showInProduction: boolean;
}

const ALL_STEPS: StepDef[] = [
    { key: 'company', label: 'companyOnboarding.steps.company', icon: <BusinessIcon />, showInDemo: true, showInProduction: true },
    { key: 'domain', label: 'companyOnboarding.steps.domain', icon: <DomainIcon />, showInDemo: true, showInProduction: true },
    { key: 'branding', label: 'companyOnboarding.steps.branding', icon: <BrandingIcon />, showInDemo: true, showInProduction: true },
    { key: 'license', label: 'companyOnboarding.steps.license', icon: <LicenseIcon />, showInDemo: false, showInProduction: true },
    { key: 'admins', label: 'companyOnboarding.steps.admins', icon: <PersonIcon />, showInDemo: true, showInProduction: true },
    { key: 'demoData', label: 'companyOnboarding.steps.demoData', icon: <DemoDataIcon />, showInDemo: true, showInProduction: false },
    { key: 'summary', label: 'companyOnboarding.steps.summary', icon: <CheckIcon />, showInDemo: true, showInProduction: true },
];

export const CompanyOnboardingWizard: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();

    const [activeStep, setActiveStep] = useState(0);
    const [data, setData] = useState<OnboardingData>(initialData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [stepErrors, setStepErrors] = useState<Record<string, string>>({});

    // Aktive Step-Liste haengt vom Demo-Toggle ab — Conditional-Visibility.
    const steps = useMemo<StepDef[]>(() => {
        return ALL_STEPS.filter(s => data.isDemoTenant ? s.showInDemo : s.showInProduction);
    }, [data.isDemoTenant]);

    const currentStep = steps[activeStep];

    const updateData = (updates: Partial<OnboardingData>) => {
        setData(prev => ({ ...prev, ...updates }));
    };

    const validateStep = (step: StepDef): boolean => {
        const errors: Record<string, string> = {};
        switch (step.key) {
            case 'company':
                if (!data.companyName.trim()) {
                    errors.companyName = translate('companyOnboarding.errors.companyNameRequired');
                }
                break;

            case 'domain':
                if (data.domainMode === 'subdomain') {
                    if (!data.subdomainSlug.trim()) {
                        errors.subdomainSlug = translate('companyOnboarding.errors.subdomainRequired');
                    } else if (!/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/i.test(data.subdomainSlug.trim())) {
                        errors.subdomainSlug = translate('companyOnboarding.errors.subdomainInvalid');
                    }
                } else {
                    if (!data.customDomain.trim()) {
                        errors.customDomain = translate('companyOnboarding.errors.customDomainRequired');
                    }
                }
                break;

            case 'license':
                if (!data.productId) {
                    errors.productId = translate('companyOnboarding.errors.productRequired');
                }
                break;

            case 'admins':
                if (data.admins.length === 0) {
                    errors.admins = translate('companyOnboarding.errors.atLeastOneAdmin');
                }
                data.admins.forEach((a, i) => {
                    if (!a.email.trim() || !/^\S+@\S+\.\S+$/.test(a.email.trim())) {
                        errors[`admin_${i}_email`] = translate('companyOnboarding.errors.adminEmailInvalid');
                    }
                });
                break;

            case 'demoData':
                if (!data.demoSourceCompanyId) {
                    errors.demoSourceCompanyId = translate('companyOnboarding.errors.demoSourceRequired');
                }
                if (data.demoExpiresInDays < 1 || data.demoExpiresInDays > 365) {
                    errors.demoExpiresInDays = translate('companyOnboarding.errors.demoExpiresRange');
                }
                break;
        }
        setStepErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setActiveStep(prev => prev + 1);
            setError(null);
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
        setError(null);
    };

    /**
     * Submit-Pipeline:
     *   1. POST /api/companies/onboard mit Branding + Domain + erstem Admin
     *   2. POST /api/companies/{id}/invite-admins fuer alle Admin-Eintraege
     *   3. (nur Demo) POST /api/operator/seed-tenant fuer die Daten-Kopie
     */
    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');

            // Domain aufloesen — Wildcard-Label haengt vom Modus + Cluster ab.
            // Architektur post v0.3.0: Demos auf Prod (.demo.), interne
            // Stage-Tests auf .demo-stage.; Production-Tenants auf .app.
            const isStageBuild = (process.env.REACT_APP_SSO_URL || '').includes('stage.');
            const wildcardLabel = data.isDemoTenant
                ? (isStageBuild ? 'demo-stage' : 'demo')
                : 'app';
            const fullDomain = data.domainMode === 'subdomain'
                ? `${data.subdomainSlug}.${wildcardLabel}.generationl-onlineacademy.de`
                : data.customDomain.trim();

            // Schritt 1: Onboarding (legt Company + ersten Admin an)
            const firstAdmin = data.admins[0];
            const onboardResp = await fetch(`${process.env.REACT_APP_SSO_URL}/api/companies/onboard`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    companyName: data.companyName,
                    companyEmail: data.companyEmail || null,
                    companyPhone: data.companyPhone || null,
                    companyDomain: fullDomain,
                    companyStreet: data.companyStreet || null,
                    companyZip: data.companyZip || null,
                    companyCity: data.companyCity || null,
                    companyCountry: data.companyCountry || null,
                    companyWebsite: data.companyWebsite || null,
                    // Branding direkt mit anlegen
                    logoUrl: data.logoUrl || null,
                    primaryColor: data.primaryColor || null,
                    secondaryColor: data.secondaryColor || null,
                    faviconUrl: data.faviconUrl || null,
                    appTitle: data.appTitle || null,
                    poweredByText: data.poweredByText || null,
                    // Demo-Felder
                    isDemoTenant: data.isDemoTenant,
                    expiresInDays: data.isDemoTenant ? data.demoExpiresInDays : null,
                    // Lizenz nur in Production-Mode
                    productId: data.isDemoTenant ? null : data.productId,
                    licenseExpiresAt: data.isDemoTenant ? null : (data.licenseExpiresAt || null),
                    maxUsers: data.isDemoTenant ? null : (data.maxUsers ? parseInt(data.maxUsers) : null),
                    // Erster Admin als Invitation
                    adminEmail: firstAdmin.email,
                    adminFirstName: firstAdmin.firstName || null,
                    adminLastName: firstAdmin.lastName || null,
                    sendInvitation: true,
                }),
            });
            const onboardResult = await onboardResp.json();
            if (!onboardResult.success) {
                throw new Error(onboardResult.error || translate('companyOnboarding.errors.unknown'));
            }
            const newCompanyId: number = onboardResult.company?.id ?? onboardResult.companyId;
            if (!newCompanyId) {
                throw new Error('Antwort enthaelt keine companyId');
            }

            // Schritt 2: weitere Admins per Bulk-Invite
            if (data.admins.length > 1) {
                const additional = data.admins.slice(1);
                await fetch(`${process.env.REACT_APP_SSO_URL}/api/companies/${newCompanyId}/invite-admins`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ admins: additional }),
                });
            }

            // Schritt 3: Demo-Daten seeden (nur Demo)
            if (data.isDemoTenant && data.demoSourceCompanyId) {
                const seedResp = await fetch(`${process.env.REACT_APP_API_URL}/api/operator/seed-tenant`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        sourceCompanyId: data.demoSourceCompanyId,
                        targetCompanyId: newCompanyId,
                        selection: data.demoSelection,
                    }),
                });
                const seedResult = await seedResp.json();
                if (!seedResult.success) {
                    // Onboarding hat schon geklappt — Seed-Fehler nur als Warnung,
                    // damit der Operator den Mandanten manuell befuellen kann
                    notify(`${translate('companyOnboarding.warnings.seedFailed')}: ${seedResult.error || ''}`, { type: 'warning' });
                } else {
                    notify(translate('companyOnboarding.success.withSeed'), { type: 'success' });
                }
            } else {
                notify(translate('companyOnboarding.success.production'), { type: 'success' });
            }

            redirect('/companies');
        } catch (err: any) {
            setError(err?.message || translate('companyOnboarding.errors.network'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        redirect('/companies');
    };

    const renderStepContent = () => {
        switch (currentStep.key) {
            case 'company':
                return <CompanyStep data={data} updateData={updateData} errors={stepErrors} />;
            case 'domain':
                return <DomainStep data={data} updateData={updateData} errors={stepErrors} />;
            case 'branding':
                return <BrandingStep data={data} updateData={updateData} errors={stepErrors} />;
            case 'license':
                return <LicenseStep data={data} updateData={updateData} errors={stepErrors} />;
            case 'admins':
                return <MultiAdminStep data={data} updateData={updateData} errors={stepErrors} />;
            case 'demoData':
                return <DemoDataStep data={data} updateData={updateData} errors={stepErrors} />;
            case 'summary':
                return <SummaryStep data={data} />;
            default:
                return null;
        }
    };

    return (
        <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
            <Title title={translate('companyOnboarding.title')} />

            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    {translate('companyOnboarding.title')}
                </Typography>

                <Stepper
                    activeStep={activeStep}
                    sx={{
                        mt: 3,
                        mb: { xs: 1, sm: 4 },
                        // Mobile (< sm): nur Icons, Label des aktiven Schritts steht darunter
                        '& .MuiStepLabel-labelContainer': { display: { xs: 'none', sm: 'block' } },
                        '& .MuiStepConnector-root': { minWidth: { xs: 4, sm: 'auto' } },
                        '& .MuiStepLabel-iconContainer': { pr: { xs: 0, sm: 1 } },
                    }}
                >
                    {steps.map((step, index) => (
                        <Step key={step.key}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Box sx={{
                                        width: 40, height: 40, borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                                        color: index <= activeStep ? 'white' : 'grey.600',
                                    }}>
                                        {step.icon}
                                    </Box>
                                )}
                            >
                                {translate(step.label)}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Typography
                    className="wizard-step-caption"
                    variant="subtitle1"
                    sx={{ display: { xs: 'block', sm: 'none' }, textAlign: 'center', mb: 3, fontWeight: 600 }}
                >
                    {steps[activeStep] ? translate(steps[activeStep].label) : ''}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ minHeight: 300 }}>
                    {renderStepContent()}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button variant="outlined" onClick={handleCancel} disabled={loading}>
                        {translate('companyOnboarding.buttons.cancel')}
                    </Button>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {activeStep > 0 && (
                            <Button variant="outlined" startIcon={<BackIcon />} onClick={handleBack} disabled={loading}>
                                {translate('companyOnboarding.buttons.back')}
                            </Button>
                        )}

                        {activeStep < steps.length - 1 ? (
                            <Button variant="contained" endIcon={<ForwardIcon />} onClick={handleNext}>
                                {translate('companyOnboarding.buttons.next')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleSubmit}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                            >
                                {translate('companyOnboarding.buttons.finish')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default CompanyOnboardingWizard;
